import { Component, OnDestroy, ChangeDetectorRef, AfterViewChecked, ViewChild, OnInit, ElementRef } from '@angular/core';
import { ActivatedRoute, Params, Router, NavigationEnd } from '@angular/router';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { CaseService } from '../../core/service/case.service';
import { Subscription } from 'rxjs';
import { TaskUtility } from '../../task/task.utility';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { CaseUtility } from '../case.utility';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { CaseDetailSummaryComponent } from './case-detail-summary/case-detail-summary.component';
import moment from 'moment';
import { SettingService } from 'src/app/core/service/setting.service';
import { LocalStorageService } from 'ngx-webstorage';
import { IntlService } from '@progress/kendo-angular-intl';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { UserService } from 'src/app/core/service/user.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { sortBy } from 'lodash';
import { PayerBillingService } from 'src/app/core/service/payer-billing.service';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { BillableService } from 'src/app/core/service/billable.service';

/**
 * Angular component for Case detail.
 */
@Component({
  standalone: false,
  selector: 'app-case-detail',
  templateUrl: './case-detail.component.html',
  styleUrls: ['./case-detail.component.scss']
})
export class CaseDetailComponent implements OnDestroy, AfterViewChecked, OnInit
{
  @ViewChild('caseDetailSummary') public caseDetailSummary: CaseDetailSummaryComponent;
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  public pageSizes = [5, 10, 25, 50, 100];
  public billablesCount: number;
  public billablesExist = false;

  /**
   * Case ID.
   */
  public id: number;

  /**
   * Data model for this component.
   */
  public model: any = {};

  /**
   * Subject/Patient of the case.
   */
  public member: any;
  public memberId = null;

  /**
   * Subscription to Angular component route.
   */
  public routeParamSubscription: Subscription;

  /**
   * Loading indicator
   */
  public loading = false;

  /**
   * Breadcrumb from route parameters.
   */
  public breadcrumb = 'ALL';

  /**
   * Icons map for Case history entries.
   */
  public eventIcons: Map<string, string> = new Map([
    ['Case', 'fas fa-clock icon-bg-dark'],
    ['Task', 'fas fa-check-circle icon-bg-medium'],
    ['Message', 'fas fa-envelope icon-bg-light'],
    ['Transaction', 'fas fa-usd-circle icon-bg-green']]);

  /**
   * History Types with the corresponding translations.
   */
  public historyTypeTexts: Array<{ type: string; text: string }>;

  /**
   * Case history events currently displayed (after filtered are applied).
   */
  public displayedHistoryEvents: any[];

  /**
   * Indicate which task should be loaded. Loading OPEN by default.
   */
  public currentTaskFilter = 'OPEN';

  public patientLabel = {};
  public editPatientDialog = false;
  public caseHistoriesCollapsed = false;
  public caseEventsCollapsed = false;
  public patientPinnedResourcesCollapsed = false;
  public showHeader = false;
  public contactsEnabled = false;
  public appointmentsEnabled = false;
  public eventsEnabled = false;
  public caseExtensionsEnabled = false;
  public caseExtensionsEditable = false;
  public notesEnabled = false;
  public caseHistoryEnabled = false;
  public caseCareTeamsEnabled = false;
  public patientCareTeamsEnabled = false;
  public pinnedResourcesEnabled = false;
  public communityEnabled = false;
  public viewExtensions: GridViewManager;
  public viewExtensionsShowDisplayName = false;
  public viewExtensionsShowAlternateValue = false;
  public viewBillableItems: GridViewManager;
  public tabCounts: any;
  public switchingCaseContext = false;
  public paneCollapsed = false;
  public isOperatorRole = false;
  public enableAdvanced = false;

  /**
   * FormGroup for in-grid editing of case extension.
   */
  public caseExtensionFormGroup: any = null;
  public currentUserOrganization: Organization;
  public organizations = [];
  public extensionOrganizations = [];
  public extensionsByCategory = [];
  public extensionsByCategoryAndOrganization = [];
  public extensionsByCategoryAndOrganizationUnused = [];

  public caseBillableFormGroup: any = null;
  /**
   * Controls the case extension deletion confirmation dialog.
   */
  public caseExtensionDeleteDialogOpened = false;

  /**
   * The selected case extension.
   */
  public selectedCaseExtension: any = null;

  public caseBillableDeleteDialogOpened = false;
  public selectedCaseBillable: any = null;
  public isBillablesTabSelected = false;

  public caseBillableAdjustmentVoidDialogOpened = false;
  public billableToAdjust: any = null;
  public event: any = null;
  public adjustmentStatus: number;
  public reason = null;
  public dateOfAdjustment: Date = null;
  public minDateOfAdjustment: Date;
  public maxDateOfAdjustment: Date;
  public visibleBillableColumn = false;

  private textManualAmountRequired: string;
  private textUnitsRequired: string;
  private textRateRequired: string;
  private textUnitsRateRequired: string;
  private textDxRequired: string;
  private textUnitsDxRequired: string;
  private textRateDxRequired: string;
  private textUnitsRateDxRequired: string;
  private textUnitsManualAmountRequired: string;
  private textManualAmountDxRequired: string;
  private textUnitsManualAmountDxRequired: string;

  /**
   * Subscriptions for messaging service
   */
  private messagingSubscription: Subscription;
  private routerSubscription: Subscription;

  /**
   * Default size of the right-side pane.
   */
  private paneSize = '25%';
  private billableStatuses = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private caseService: CaseService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private eventListService: EventListService,
    private taskUtilityService: TaskUtility,
    private caseUtilityService: CaseUtility,
    private settingService: SettingService,
    private userService: UserService,
    private storageService: LocalStorageService,
    private formBuilder: UntypedFormBuilder,
    private organizationService: OrganizationService,
    private extensionService: ExtensionService,
    private payerBillingService: PayerBillingService,
    private billableService: BillableService,
    public intl: IntlService)
  {
    this.isOperatorRole = this.authenticationService.currentUser.role === 'ROLE_OPERATOR';
    this.paneSize = this.storageService.retrieve('case-detail-sidebar-size') || '25%';

    this.viewExtensions = new GridViewManager(99999);
    this.viewExtensions.setData([]);

    this.viewBillableItems = new GridViewManager(99999);
    this.viewBillableItems.setData([]);

    this.extensionService.getDistinctExtensionOrganizations('CASE').subscribe({
      next: (data) =>
      {
        this.extensionOrganizations = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    // Get user organization
    const user = this.authenticationService.currentUser;
    this.userService.getUserById(user.id, false).subscribe({
      next: (data) =>
      {
        this.currentUserOrganization = data.organization;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    // Get Organizations
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
      }
    });

    this.routeParamSubscription = this.route.params.subscribe({
      next: (params: Params) =>
      {
        this.switchingCaseContext = true;
        this.id = +params.id;
        this.loadCaseDetails();
      }
    });

    this.route.queryParams.subscribe({
      next: (p) =>
      {
        this.breadcrumb = p.breadcrumb;
      }
    });
  }

  public get rightPaneSize(): string
  {
    return this.paneSize;
  }

  public set rightPaneSize(newSize: string)
  {
    this.paneSize = newSize;
    this.storageService.store('case-detail-sidebar-size', newSize);
    this.autoExpandCollapseHistory();
  }

  /**
   * Extension organization changed.
   */
  public organizationChange(reset: boolean): void
  {
    if (reset)
    {
      this.extensionsByCategory = [];
      this.extensionsByCategoryAndOrganization = [];
      this.extensionsByCategoryAndOrganizationUnused = [];
      this.caseExtensionFormGroup.get('settingExtension').setValue(null);
    }

    if (this.caseExtensionFormGroup.get('organization').value)
    {
      this.extensionService.getExtensionsByCategory('CASE').subscribe({
        next: (data) =>
        {
          this.extensionsByCategory = data;
          this.extensionsByCategoryAndOrganization = this.extensionsByCategory.filter(
            extension => extension.organization === null || extension.organization.id === this.caseExtensionFormGroup.get('organization').value.id);
          const extensionsInUse = this.viewExtensions.gridData.data.filter(extension =>
            extension.id !== (this.caseExtensionFormGroup.get('id').value ? this.caseExtensionFormGroup.get('id').value.id : null));
          this.extensionsByCategoryAndOrganizationUnused = sortBy(
            this.extensionsByCategoryAndOrganization.filter(u => !extensionsInUse.map(m => m.settingExtension.id).includes(u.id)), ['name']);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  public loadCaseDetails(): void
  {
    this.translate.get([
      'HISTORY-TYPE-CASE-OPENED',
      'HISTORY-TYPE-CASE-CLOSED',
      'HISTORY-TYPE-CASE-MANAGER-CHANGED',
      'HISTORY-TYPE-CASE-PRIORITY-CHANGED',
      'HISTORY-TYPE-TASK-OPENED',
      'HISTORY-TYPE-TASK-COMPLETED',
      'HISTORY-TYPE-TASK-CLOSED',
      'HISTORY-TYPE-MESSAGE-CREATED',
      'HISTORY-TYPE-DOCUMENT-ATTACHED',
      'HISTORY-TYPE-DOCUMENT-DELETED',
      'HISTORY-TYPE-TASK-PRIORITY-CHANGED',
      'HISTORY-TYPE-TASK-ASSIGNED',
      'HISTORY-TYPE-TASK-UNASSIGNED',
      'HISTORY-TYPE-TASK-MODIFIED',
      'HISTORY-TYPE-TASK-RESCHEDULED',
      'HISTORY-TYPE-CASE-STATUS-CHANGED',
      'HISTORY-TYPE-CASE-WORKFLOW-STATUS-CHANGED',
      'HISTORY-TYPE-FOLLOW-UP-TASK-OPENED',
      'HISTORY-TYPE-CASE-SENSITIVITIES-CHANGED',
      'HISTORY-TYPE-CASE-BILLABLE-SERVICE-CREATED',
      'MANUAL-AMOUNT-REQUIRED',
      'UNITS-REQUIRED',
      'RATE-REQUIRED-BILLABLE',
      'UNITS-RATE-REQUIRED',
      'DX-REQUIRED-BILLABLE',
      'UNITS-DX-REQUIRED',
      'RATE-DX-REQUIRED-BILLABLE',
      'UNITS-RATE-DX-REQUIRED',
      'UNITS-MANUAL-AMOUNT-REQUIRED',
      'MANUAL-AMOUNT-DX-REQUIRED-BILLABLE',
      'UNITS-MANUAL-AMOUNT-DX-REQUIRED'
    ]).subscribe({
      next: (res) =>
      {
        this.textManualAmountRequired = res['MANUAL-AMOUNT-REQUIRED'];
        this.textUnitsRequired = res['UNITS-REQUIRED'];
        this.textRateRequired = res['RATE-REQUIRED-BILLABLE'];
        this.textUnitsRateRequired = res['UNITS-RATE-REQUIRED'];
        this.textDxRequired = res['DX-REQUIRED-BILLABLE'];
        this.textUnitsDxRequired = res['UNITS-DX-REQUIRED'];
        this.textRateDxRequired = res['RATE-DX-REQUIRED-BILLABLE'];
        this.textUnitsRateDxRequired = res['UNITS-RATE-DX-REQUIRED'];
        this.textUnitsManualAmountRequired = res['UNITS-MANUAL-AMOUNT-REQUIRED'];
        this.textManualAmountDxRequired = res['MANUAL-AMOUNT-DX-REQUIRED-BILLABLE'];
        this.textUnitsManualAmountDxRequired = res['UNITS-MANUAL-AMOUNT-DX-REQUIRED'];
        this.historyTypeTexts = [];
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            // Name of translation keys matches the history type after removing prefix and replace - by _
            const historyType = key.replace('HISTORY-TYPE-', '').replace(/-/g, '_');
            this.historyTypeTexts.push({ type: historyType, text: res[key] });
          }
        }

        if (this.routerSubscription === undefined)
        {
          this.routerSubscription = this.router.events.subscribe({
            next: (event) =>
            {
              if (event instanceof NavigationEnd)
              {
                window.scrollTo(0, 0);
              }
            }
          });
        }

        // subscribe to case manager update messages.
        if (this.messagingSubscription === undefined)
        {
          this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
            next: (message) =>
            {
              switch (message.type)
              {
                case 'taskFilterChanged':
                  this.currentTaskFilter = message.data.filter;
                  this.getCaseDetailsData();
                  break;
                case 'caseManagerUpdated':
                case 'casePriorityUpdated':
                case 'caseHistoryUpdated':
                case 'getCaseDetailsData':
                  setTimeout(() => { this.getCaseDetailsData(); }, 2500);
                  break;
                case 'timelineVisibilityChanged':
                  this.displayedHistoryEvents = this.model.caseHistoryEvents
                    .filter(e => message.data.includeTasksInTimeline || !e.type.includes('TASK'))
                    .filter(e => message.data.includeMessagesInTimeline || !e.type.includes('MESSAGE'));
                  break;
                case 'openEditCareTeamMemberCaseDetails':
                  setTimeout(() =>
                  {
                    this.messengerService.sendMessage(new MessageSubject('openCareTeamMemberCase', message.data));
                  });
              }
            }
          });
        }

        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting) =>
          {
            this.patientLabel = { patientLabel: sessionSetting.patientLabel };
            this.communityEnabled = sessionSetting.communityEnabled;
            this.appointmentsEnabled = (sessionSetting.appointmentsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.appointmentsRoles) : false;
            this.contactsEnabled = (sessionSetting.contactsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.contactsRoles) : false;
            this.notesEnabled = (sessionSetting.notesRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.notesRoles) : false;
            this.eventsEnabled = (sessionSetting.eventsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.eventsRoles) : false;
            this.caseExtensionsEnabled = (sessionSetting.caseExtensionsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.caseExtensionsRoles) : false;
            this.caseExtensionsEditable = (sessionSetting.addUpdateExtensionsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addUpdateExtensionsRoles) : false;
            this.caseHistoryEnabled = (sessionSetting.caseHistoryRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.caseHistoryRoles) : false;
            this.caseCareTeamsEnabled = (sessionSetting.caseCareTeamsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.caseCareTeamsRoles) : false;
            this.patientCareTeamsEnabled = (sessionSetting.patientCareTeamsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.patientCareTeamsRoles) : false;
            this.pinnedResourcesEnabled = (sessionSetting.pinnedResourcesRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.pinnedResourcesRoles) : false;

            this.getCaseDetailsData();
            this.saveRecentView();
          }
        });
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
    this.updateSplitterBar();
  }

  public ngOnInit(): void
  {
    this.billableStatuses = this.payerBillingService.invoiceItemStatuses;
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (this.routerSubscription !== undefined) { this.routerSubscription.unsubscribe(); }
  }

  public getCaseBillableText(billableValue: string): string 
  {
    const billableStatus = this.billableStatuses.find(x => x.value === billableValue);
    if (billableStatus)
    {
      billableValue = billableStatus.label;
    }
    return billableValue;
  }

  /**
   * Get the related data for the Case from REST services.
   */
  public getCaseDetailsData(): void
  {
    this.loading = true;

    // get Case details data
    this.caseService.getCaseDetails(this.id, this.currentTaskFilter).subscribe({
      next: (data) =>
      {
        const now = new Date();
        data.tasks.forEach(task =>
        {
          task.openedOn = new Date(task.openedOn);
          if (task.dueDate != null)
          {
            task.dueDate = new Date(task.dueDate);
            const a = moment(Date.UTC(task.dueDate.getFullYear(), task.dueDate.getMonth(), task.dueDate.getDate()));
            const b = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
            task.daysLeft = a.diff(b, 'days');
          }
          task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
          task.priority = this.taskUtilityService.priority(task.priority);
          task.eventTypeIcon = this.eventListService.getEventTypeIcon(task.eventType);
          task.eventTypeText = this.eventListService.getEventTypeText(task.eventType);
          task.encounterTypeText = this.eventListService.getEncounterTypeText(task.encounterType);
          task.inCaseDetail = true; // indicates the task-detail component is shown in the Case-detail component.
        });

        data.messages.forEach(message =>
        {
          message.sentOn = new Date(message.sentOn);
        });

        data.caseHistoryEvents.forEach((e: any) =>
        {
          e.time = new Date(e.time);
          e.icon = this.eventIcons.get(e.type);
          e.title = this.getHistoryTitle(e);
        });

        this.model = data;
        this.model.priorityKey = this.caseUtilityService.priorityKey(data.priority);
        this.member = data.member;
        this.memberId = data.member.id;
        this.showHeader = true;

        // initially, all events are displayed in the timeline.
        this.displayedHistoryEvents = data.caseHistoryEvents;

        // Set Case Extensions
        this.viewExtensions.setData(data.extensions ? data.extensions : []);
        this.viewExtensions.setSort([{ field: 'settingExtension.name', dir: 'asc' }]);
        this.setViewExtensionsColumnsVisibility();

        this.billablesExist = data.billablesExist;

        // Get counts
        this.getCaseCountTabs();
        this.autoExpandCollapseHistory();
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.loading = false;
        this.switchingCaseContext = false;
      }
    });
  }

  /**
   * Handles the add new row event of the Kendo grid for case extension.
   * Adds a new row to the grid, and creates a FormGroup for data input.
   */
  public caseExtensionAddHandler(event: GridEventHandler): void
  {
    // Get the default organization
    let defaultOrganization: Organization;
    if (this.currentUserOrganization !== undefined)
    {
      defaultOrganization = this.organizations.find(x => x.id === this.currentUserOrganization.id);
    }

    this.caseExtensionFormGroup = this.formBuilder.group(
      {
        id: null,
        organization: [{
          value: defaultOrganization,
          disabled: false
        }, Validators.required],
        settingExtension: [{ value: null, disabled: false }, Validators.required],
        value: [{ value: null, disabled: false }, Validators.required],
        codeSetId: null,
        editing: true
      }
    );

    event.sender.addRow(this.caseExtensionFormGroup);

    if (defaultOrganization !== undefined)
    {
      this.organizationChange(false);
    }
  }

  /**
   * Handles the edit event of the case extensions grid.
   */
  public caseExtensionEditHandler(event: GridEventHandler): void
  {
    this.caseExtensionFormGroup = this.formBuilder.group(
      {
        isNew: false,
        id: event.dataItem.id,
        organization: [event.dataItem.settingExtension.organization, Validators.required],
        value: [event.dataItem.value, Validators.required],
        settingExtension: [event.dataItem.settingExtension, Validators.required],
        codeSetId: event.dataItem.codeSetId,
        editing: true,
      }
    );
    this.organizationChange(false);
    this.caseExtensionFormGroup.get('value').valueChanges.subscribe({ next: (value: string) => { this.refreshExtensionDisplayNames(value); } });
    event.sender.editRow(event.rowIndex, this.caseExtensionFormGroup);
  }

  /**
   * Handles the edit event of the case billable grid.
   */
  public caseBillableEditHandler(event: GridEventHandler): void
  {
    this.caseBillableFormGroup = this.formBuilder.group(
      {
        id: event.dataItem.id,
        units: event.dataItem.units,
        rate: event.dataItem.rate,
        amount: event.dataItem.amount,
        billableServiceHasManualAmount: event.dataItem.billableServiceHasManualAmount,
        diagnoses: event.dataItem.diagnoses,
        editing: true,
      }
    );

    event.sender.editRow(event.rowIndex, this.caseBillableFormGroup);
  }

  /**
   * Opens the delete case extension confirmation dialog.
   *
   * @param caseExtension The case extension to delete.
   */
  public openDeleteCaseExtensionDialog(caseExtension: any): void
  {
    this.selectedCaseExtension = caseExtension;
    this.caseExtensionDeleteDialogOpened = true;
  }

  /**
   * Deletes a case extension.
   *
   * @param caseExtension ID of the case extension to delete.
   */
  public deleteCaseExtension(caseExtension: any): void
  {
    this.caseService.deleteCaseExtension(caseExtension.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CASE-EXTENSION-DELETED-SUCCESSFULLY');
        this.viewExtensions.removeRow(caseExtension);
        this.setViewExtensionsColumnsVisibility();
        this.caseExtensionDeleteDialogOpened = false;
        this.selectedCaseExtension = null;
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  /**
   * Opens the delete case billable confirmation dialog.
   *
   * @param caseBillable The case billable to delete.
   */
  public openDeleteCaseBillableDialog(caseBillable: any): void
  {
    this.selectedCaseBillable = caseBillable;
    this.caseBillableDeleteDialogOpened = true;
  }

  /**
   * Deletes a case billable.
   *
   * @param caseBillable ID of the case billable to delete.
   */
  public deleteCaseBillable(caseBillable: any): void
  {
    this.billableService.deleteCaseBillable(caseBillable.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CASE-BILLABLE-DELETED-SUCCESSFULLY');
        this.viewBillableItems.removeRow(caseBillable);
        if (this.viewBillableItems.data.length === 0)
        {
          this.tabstrip.selectTab(0);
        }
        this.caseBillableDeleteDialogOpened = false;
        this.selectedCaseBillable = null;
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  public showDetailsOnlyIfCaseAccessible = (dataItem: any) =>
  {
    return dataItem !== undefined && dataItem.alreadyAdjustedOrVoided;
  };

  /**
   * Saves the edited case extension from the grid.
   *
   * @param event Save row edit event from the Kendo grid for case extension.
   */
  public saveCaseExtension(event): void
  {
    if (event.isNew)
    {
      // constructs a new case extension object
      const addExtension =
      {
        organizationId: event.dataItem.organization.id,
        key: event.dataItem.settingExtension.key,
        value: event.dataItem.value,
        codeSetId: null
      };

      // call caseService to create the new case extension record on the server.
      this.caseService.addCaseExtension(addExtension, this.id).subscribe({
        next: (data: any) =>
        {
          this.messageHandlerService.displaySuccess('CASE-EXTENSION-SAVED-SUCCESSFULLY');

          // update the item with the server generated ID
          event.dataItem = data;

          // close the editing row, and update the grid.
          this.viewExtensions.saveHandler(event);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    else
    {
      const caseExtension = this.caseExtensionFormGroup.value;
      caseExtension.caseId = this.id;
      caseExtension.organizationId = this.caseExtensionFormGroup.get('organization').value.id;

      delete caseExtension.editing;
      delete caseExtension.name;
      delete caseExtension.organization;

      // update the case extension
      this.caseService.updateCaseExtension(caseExtension, caseExtension.id).subscribe({
        next: (data: any) =>
        {
          this.messageHandlerService.displaySuccess('CASE-EXTENSION-SAVED-SUCCESSFULLY');

          event.dataItem = data;

          // close the editing row, and update the grid.
          this.viewExtensions.saveHandler(event);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
  }

  /**
   * Saves the edited case billable from the grid.
   *
   * @param event Save row edit event from the Kendo grid for case billable.
   */
  public saveCaseBillable(event): void
  {
    this.selectedCaseBillable = event.dataItem;
    this.event = event;
    this.billableToAdjust = this.caseBillableFormGroup.value;
    if (this.adjustmentStatus === -1) // edit
    {
      const caseBillable = this.caseBillableFormGroup.value;
      delete caseBillable.editing;

      if (this.caseBillableValidation(event, caseBillable))
      {
        // update the case billable
        this.billableService.updateCaseBillable(caseBillable, caseBillable.id, this.memberId).subscribe({
          next: (data: any) =>
          {
            this.messageHandlerService.displaySuccess('CASE-BILLABLE-SAVED-SUCCESSFULLY');

            event.dataItem = data;
            if (event.dataItem.billableDate) { event.dataItem.billableDate = new Date(`${event.dataItem.billableDate}T00:00:00.000`); }

            // close the editing row, and update the grid.
            this.viewBillableItems.saveHandler(event);
          },
          error: (error) =>
          {
            this.logger.error(error);
            this.messageHandlerService.displayError(error);
          }
        });
      }
    }
    else
      if (this.adjustmentStatus === 0)
      {
        this.minDateOfAdjustment = moment(this.selectedCaseBillable.billableDate, 'YYYY-MM-DD').toDate();
        this.maxDateOfAdjustment = moment().toDate();
        this.dateOfAdjustment = moment().toDate();
        this.caseBillableAdjustmentVoidDialogOpened = true;
      }
  }

  public cancelBillableAdjustment(): void
  {
    this.caseBillableAdjustmentVoidDialogOpened = false;
    this.reason = null;
    this.dateOfAdjustment = null;
    this.caseBillableCancelHandler(this.event);
  }

  public adjustBillable(): void
  {
    if (this.adjustmentStatus === 0) // adjustment
    {
      const caseBillable = this.caseBillableFormGroup.value;
      delete caseBillable.editing;

      if (!this.caseBillableValidation(this.event, caseBillable))
      {
        return;
      }
    }

    const id = this.selectedCaseBillable.id;

    const billable = this.selectedCaseBillable;

    if (this.adjustmentStatus === 0) // adjustment
    {
      billable.units = this.billableToAdjust.units;
      billable.rate = this.billableToAdjust.rate;
      billable.amount = this.billableToAdjust.amount;
      billable.diagnoses = this.billableToAdjust.diagnoses;
    }
    billable.adjustmentDate = this.dateOfAdjustment;
    billable.adjustmentReason = this.reason;
    billable.adjustmentStatus = this.adjustmentStatus;
    billable.parentId = billable.id;
    billable.id = 0;

    this.billableService.adjustCaseBillable(billable, this.memberId, id, this.adjustmentStatus).subscribe({
      next: () =>
      {
        if (this.adjustmentStatus === 0)
        {
          this.messageHandlerService.displaySuccess('CASE-BILLABLE-ADJUSTED-SUCCESSFULLY');
        }
        else
          if (this.adjustmentStatus === 1)
          {
            this.messageHandlerService.displaySuccess('CASE-BILLABLE-VOIDED-SUCCESSFULLY');
          }

        if (this.adjustmentStatus === 0) // adjustment
        {
          // close the editing row, and update the grid.
          this.viewBillableItems.saveHandler(this.event);
        }

        this.loadCaseBillable();
        this.caseBillableAdjustmentVoidDialogOpened = false;
        this.reason = null;
        this.dateOfAdjustment = null;

        // refresh grid
      },
      error: error =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      }
    });
  }

  public openVoidCaseBillableDialog(caseBillable: any): void
  {
    this.selectedCaseBillable = caseBillable;
    this.adjustmentStatus = 1;
    this.minDateOfAdjustment = moment(this.selectedCaseBillable.billableDate, 'YYYY-MM-DD').toDate();
    this.maxDateOfAdjustment = moment().toDate();
    this.dateOfAdjustment = moment().toDate();
    this.caseBillableAdjustmentVoidDialogOpened = true;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public extensionsDataStateChange(state: DataStateChangeEvent): void
  {
    this.viewExtensions.setState(state);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public billableItemsDataStateChange(state: DataStateChangeEvent): void
  {
    this.viewBillableItems.setState(state);
  }

  public caseBillableCancelHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Get case counts. All showing member counts except notes.
   */
  public getCaseCountTabs(): void
  {
    this.caseService.getCaseCountTabs(this.id).subscribe({
      next: (data) =>
      {
        this.tabCounts = data;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public collapseChange(e: boolean): void
  {
    this.paneCollapsed = e;
  }

  public onTabSelect(e: SelectEvent): void
  {
    this.isBillablesTabSelected = e.title === this.translate.instant('BILLABLES');
    if (this.isBillablesTabSelected)
    {
      this.loadCaseBillable();
    }
  }

  public loadCaseBillable(): void
  {
    this.visibleBillableColumn = false;
    this.billableService.getCaseBillable(this.memberId, this.id).subscribe({
      next: (data: any) =>
      {
        data.forEach((d: any) =>
        {
          // Build modifierCodes just for filtering purpose
          d.modifierCodes = '';
          if (d.modifierCode) { d.modifierCodes += d.modifierCode }
          if (d.modifierCode2) { d.modifierCodes += d.modifierCode2 }
          if (d.modifierCode3) { d.modifierCodes += d.modifierCode3 }
          // Make the billable expand / collapse column visible only if there are adjustments
          if (d.alreadyAdjustedOrVoided)
          {
            this.visibleBillableColumn = true;
          }
          if (d.billableDate) { d.billableDate = new Date(`${d.billableDate}T00:00:00.000`); }
        })

        this.viewBillableItems.setData(data ? data : []);
        this.viewBillableItems.setSort([{ field: 'billableName', dir: 'asc' }]);
        this.billablesCount = this.viewBillableItems.data.length;
      },
      error: (errorMessage) =>
      {
        this.messageHandlerService.displayError(errorMessage);
      }
    });
  }

  public isOrganizationHasExtension(id: number): boolean 
  {
    return this.extensionOrganizations.map(x => x.id).includes(id);
  }

  public formatExtensionValue(dataItem: any): string
  {
    return this.extensionService.formatExtensionValue(dataItem);
  }

  public updateSplitterBar()
  {
    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetWidth : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  /**
   * Get the translated title for the history type.
   *
   * @param history History object
   * @returns string Translated history type
   */
  private getHistoryTitle(history: any): string
  {
    let title = history.type;
    if (history.type === 'CASE_HISTORY_RESTAPI')
    {
      title = history.title;
    }
    else
    {
      const item = this.historyTypeTexts.find(obj => obj.type === history.type);
      if (item !== undefined)
      {
        title = item.text;
      }
    }
    return title;
  }

  private autoExpandCollapseHistory(): void
  {
    if (this.model && this.model.caseHistoryEvents && this.paneSize)
    {
      const size = +this.paneSize.replace('%', '');
      this.model.caseHistoryEvents.forEach(e => { e.open = size >= 30; });
    }
  }

  private saveRecentView(): void
  {
    this.caseService.saveRecentView(this.id).subscribe({
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private setViewExtensionsColumnsVisibility(): void 
  {
    this.viewExtensionsShowDisplayName = this.viewExtensions.data.filter(x => x.displayName).length > 0;
    this.viewExtensionsShowAlternateValue = this.viewExtensions.data.filter(x => x.alternateValue).length > 0;
  }

  private refreshExtensionDisplayNames(value: string): void
  {
    this.extensionService.refreshExtensionDisplayNames(value, this.caseExtensionFormGroup, this.viewExtensions);
  }

  private caseBillableValidation(event: any, caseBillable: any): boolean
  {
    if (event.dataItem.billableServiceHasManualAmount && (!caseBillable.units || !caseBillable.amount || !caseBillable.diagnoses))
    {
      if (!caseBillable.units && !caseBillable.amount && !caseBillable.diagnoses)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.textUnitsManualAmountDxRequired.replace('{{value}}', event.dataItem.billableName));
        this.caseBillableCancelHandler(event);
        return false;
      }
      else
        if (!caseBillable.units && !caseBillable.amount)
        {
          this.messageHandlerService.displayDirectMessage('error',
            this.textUnitsManualAmountRequired.replace('{{value}}', event.dataItem.billableName));
          this.caseBillableCancelHandler(event);
          return false;
        }
        else
          if (!caseBillable.units && !caseBillable.diagnoses)
          {
            this.messageHandlerService.displayDirectMessage('error',
              this.textUnitsDxRequired.replace('{{value}}', event.dataItem.billableName));
            this.caseBillableCancelHandler(event);
            return false;
          }
          else
            if (!caseBillable.amount && !caseBillable.diagnoses)
            {
              this.messageHandlerService.displayDirectMessage('error',
                this.textManualAmountDxRequired.replace('{{value}}', event.dataItem.billableName));
              this.caseBillableCancelHandler(event);
              return false;
            }
            else
              if (!caseBillable.units)
              {
                this.messageHandlerService.displayDirectMessage('error',
                  this.textUnitsRequired.replace('{{value}}', event.dataItem.billableName));
                this.caseBillableCancelHandler(event);
                return false;
              }
              else
                if (!caseBillable.amount)
                {
                  this.messageHandlerService.displayDirectMessage('error',
                    this.textManualAmountRequired.replace('{{value}}', event.dataItem.billableName));
                  this.caseBillableCancelHandler(event);
                  return false;
                }
                else
                  if (!caseBillable.diagnoses)
                  {
                    this.messageHandlerService.displayDirectMessage('error',
                      this.textDxRequired.replace('{{value}}', event.dataItem.billableName));
                    this.caseBillableCancelHandler(event);
                    return false;
                  }
    }
    else
      if (!event.dataItem.billableServiceHasManualAmount && (!caseBillable.units || !caseBillable.rate || !caseBillable.diagnoses))
      {
        if (!caseBillable.units && !caseBillable.rate && !caseBillable.diagnoses)
        {
          this.messageHandlerService.displayDirectMessage('error',
            this.textUnitsRateDxRequired.replace('{{value}}', event.dataItem.billableName));
          this.caseBillableCancelHandler(event);
          return false;
        }
        else
          if (!caseBillable.units && !caseBillable.rate)
          {
            this.messageHandlerService.displayDirectMessage('error',
              this.textUnitsRateRequired.replace('{{value}}', event.dataItem.billableName));
            this.caseBillableCancelHandler(event);
            return false;
          }
          else
            if (!caseBillable.units && !caseBillable.diagnoses)
            {
              this.messageHandlerService.displayDirectMessage('error',
                this.textUnitsDxRequired.replace('{{value}}', event.dataItem.billableName));
              this.caseBillableCancelHandler(event);
              return false;
            }
            else
              if (!caseBillable.rate && !caseBillable.diagnoses)
              {
                this.messageHandlerService.displayDirectMessage('error',
                  this.textRateDxRequired.replace('{{value}}', event.dataItem.billableName));
                this.caseBillableCancelHandler(event);
                return false;
              }
              else
                if (!caseBillable.units)
                {
                  this.messageHandlerService.displayDirectMessage('error',
                    this.textUnitsRequired.replace('{{value}}', event.dataItem.billableName));
                  this.caseBillableCancelHandler(event);
                  return false;
                }
                else
                  if (!caseBillable.rate)
                  {
                    this.messageHandlerService.displayDirectMessage('error',
                      this.textRateRequired.replace('{{value}}', event.dataItem.billableName));
                    this.caseBillableCancelHandler(event);
                    return false;
                  }
                  else
                    if (!caseBillable.diagnoses)
                    {
                      this.messageHandlerService.displayDirectMessage('error',
                        this.textDxRequired.replace('{{value}}', event.dataItem.billableName));
                      this.caseBillableCancelHandler(event);
                      return false;
                    }
      }
    return true;
  }
}
